import React from 'react';
import DashboardContainer from '@/components/layout/DashboardContainer';
import { TwoFactorSettings } from '@/components/profile/TwoFactorSettings';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { User, Shield, Mail } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { User as UserType } from '@/types/user';

// Helper function to get user type display name
const getUserTypeDisplayName = (user: UserType | null): string => {
  if (!user) return 'User';
  
  switch (user.userType) {
    case 'employer':
      return 'Employer';
    case 'nanny':
      return 'Nanny';
    case 'CONTRACTOR':
      return 'Contractor';
    case 'CLIENT':
      return 'Client';
    case 'PARTNER':
      return 'Partner';
    case 'superuser':
      return 'Super User';
    default:
      return 'User';
  }
};

export const Profile: React.FC = () => {
  const { user } = useAuth();

  return (
    <DashboardContainer>
      <div className="container mx-auto py-6 space-y-6" data-testid="profile-page">
        {/* Page Header */}
        <div className="space-y-2">
          <h1 className="text-2xl font-medium tracking-tight">Profile Settings</h1>
          <p className="text-muted-foreground">
            Manage your account settings and security preferences
          </p>
        </div>

        <div className="grid gap-6">
          {/* Account Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                Account Information
              </CardTitle>
              <CardDescription>
                Your basic account details
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary text-primary-foreground rounded-full flex items-center justify-center">
                  <Mail className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-sm font-medium">Email Address</p>
                  <p className="text-sm text-muted-foreground">{user?.email}</p>
                </div>
              </div>
              
              <Separator />
              
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-secondary text-secondary-foreground rounded-full flex items-center justify-center">
                  <User className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-sm font-medium">Account Type</p>
                  <p className="text-sm text-muted-foreground">
                    {getUserTypeDisplayName(user)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Security Settings */}
          <div className="space-y-2">
            <h2 className="text-lg font-medium flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Security Settings
            </h2>
            <p className="text-sm text-muted-foreground">
              Protect your account with additional security measures
            </p>
          </div>

          {/* Two-Factor Authentication */}
          <TwoFactorSettings />

          {/* Future Security Settings */}
          <Card>
            <CardHeader>
              <CardTitle>Password</CardTitle>
              <CardDescription>
                Change your account password
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Use the "Forgot Password" link on the login page to reset your password.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardContainer>
  );
};